import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import {
  adminCancelDowngradeRequest,
  approveDowngradeRequest,
  cancelDowngradeRequest,
  getDowngradeRequestForTicket
} from '../../api/billingApi'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { DarkButton, OutlineButton, Pill } from './BillingUi'
import { formatBillingDate } from './billingView'

// What each request status reads as on the ticket.
export const DOWNGRADE_STATUS = {
  PENDING: { label: 'Waiting for approval', tone: 'amber' },
  SCHEDULED: { label: 'Approved', tone: 'blue' },
  COMPLETED: { label: 'Completed', tone: 'green' },
  CANCELED_BY_CLIENT: { label: 'Cancelled by client', tone: 'slate' },
  CANCELED_BY_ADMIN: { label: 'Cancelled by CarADAS', tone: 'slate' },
  FAILED: { label: 'Failed', tone: 'red' }
}

/**
 * The plan downgrade a support ticket carries (Billing → Request downgrade), shown on the ticket.
 * CarADAS admins approve it — the smaller plan is booked in Stripe for the end of the paid period —
 * or cancel it; the center's owners and managers can cancel it while it waits. Renders nothing for
 * an ordinary ticket. `onChanged` lets the ticket page reload (the ticket is closed with a note).
 */
export const DowngradeRequestPanel = ({ ticketId, isAdmin = false, canManage = false, onChanged }) => {
  const [request, setRequest] = useState(null)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(null) // 'approve' | 'cancel'
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      setRequest(await getDowngradeRequestForTicket(ticketId))
    } catch {
      setRequest(null) // not a downgrade ticket, or not visible: show nothing
    }
  }, [ticketId])

  useEffect(() => {
    if (ticketId) load()
  }, [ticketId, load])

  if (!request) return null

  const status = DOWNGRADE_STATUS[request.status] ?? { label: request.status, tone: 'slate' }
  const pending = request.status === 'PENDING'
  const scheduled = request.status === 'SCHEDULED'

  const run = async (action) => {
    setBusy(true)
    setError(null)
    try {
      setRequest(await action(request.id))
      setConfirming(null)
      onChanged?.()
    } catch (err) {
      setError(getFriendlyErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const cancelAction = isAdmin ? adminCancelDowngradeRequest : cancelDowngradeRequest
  const canCancel = isAdmin ? pending || scheduled : canManage && pending

  return (
    <section aria-label='Plan downgrade request' className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-5 py-4'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='min-w-0'>
          <div className='text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>Plan downgrade request</div>
          <div className='mt-1 flex flex-wrap items-center gap-2 text-[16px] font-semibold text-[#0A1628]'>
            <span>{request.fromTierName}</span>
            <ArrowRight className='h-4 w-4 text-[#6B7280]' aria-hidden='true' />
            <span>{request.toTierName}</span>
            <Pill label={status.label} tone={status.tone} />
          </div>
          <div className='mt-1 text-[13px] text-[#6B7280]'>
            Requested {formatBillingDate(request.requestedAt)}
            {scheduled && request.effectiveAt && ` · Changes on ${formatBillingDate(request.effectiveAt)} (end of the paid period)`}
            {!pending && !scheduled && request.decidedAt && ` · ${status.label} ${formatBillingDate(request.decidedAt)}`}
          </div>
          {pending && isAdmin && (
            <div className='mt-1 text-[12px] text-[#6B7280]'>
              Approving books the change in Stripe for the end of the period they&apos;ve paid for — no refund or credit.
            </div>
          )}
        </div>

        {(isAdmin && pending) || canCancel ? (
          <div className='flex flex-wrap items-center gap-2'>
            {confirming === 'approve' ? (
              <>
                <span className='text-[13px] text-[#0A1628]'>Approve this downgrade?</span>
                <DarkButton onClick={() => run(approveDowngradeRequest)} disabled={busy}>
                  {busy ? 'Approving…' : 'Yes, approve'}
                </DarkButton>
                <OutlineButton onClick={() => setConfirming(null)} disabled={busy}>
                  Back
                </OutlineButton>
              </>
            ) : confirming === 'cancel' ? (
              <>
                <span className='text-[13px] text-[#0A1628]'>Cancel this downgrade request?</span>
                <DarkButton onClick={() => run(cancelAction)} disabled={busy}>
                  {busy ? 'Cancelling…' : 'Yes, cancel it'}
                </DarkButton>
                <OutlineButton onClick={() => setConfirming(null)} disabled={busy}>
                  Back
                </OutlineButton>
              </>
            ) : (
              <>
                {isAdmin && pending && <DarkButton onClick={() => setConfirming('approve')}>Approve downgrade</DarkButton>}
                {canCancel && (
                  <OutlineButton onClick={() => setConfirming('cancel')}>
                    {isAdmin ? (scheduled ? 'Cancel scheduled change' : 'Cancel request') : 'Cancel downgrade'}
                  </OutlineButton>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>
      {error && <div className='mt-3 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#B91C1C]'>{error}</div>}
    </section>
  )
}
DowngradeRequestPanel.propTypes = {
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isAdmin: PropTypes.bool,
  canManage: PropTypes.bool,
  onChanged: PropTypes.func
}
